<template>
  <div
    v-if="isReady"
    class="pricing-plans text-center relative flex flex-col space-y-4 self-center w-full max-w-7xl px-6 lg:px-4"
  >
    <TypeDisplay variant="peta" weight="strong" tag="p">
      Flexible plans and features. Honest pricing. No hidden fees.
    </TypeDisplay>

    <div class="flex flex-col justify-center self-center shadow-container">
      <div class="flex-col hidden xl:flex">
        <TypeBody variant="medium" weight="regular" tag="p" class="my-4 mb-1">
          How many employees do you have?
        </TypeBody>

        <Pills
          :options="planTabs"
          :current-option="currentPlan"
          class="scroll-mt-24 justify-center self-center"
          @handleTabChange="handleTabChange"
        />
      </div>

      <div
        class="flex flex-col items-center w-full xl:hidden justify-center my-4 pb-4"
      >
        <EmployeeSelect
          :options="planOptionsDropdown"
          :current-option="currentPlan"
          @handleTabChange="handleTabChange"
        />
      </div>

      <Carousel
        id="plan-container"
        class="mb-4 flex flex-row max-w-[380px] md:max-w-3xl xl:max-w-6xl scroll-mt-12"
        :scroll-to="scrollTo"
      >
        <PlanBlock
          v-for="(plan, index) in pricingPlans"
          :id="`plan-${plan.value}`"
          :plan-id="plan.value"
          :key="plan.value"
          :is-current="currentPlan === plan.value"
          class="plan-block allPlans scroll-mt-12 scroll-pt-4 mx-2 py-4 my-4"
          :class="`plan-${index}`"
          :is-enterprise="plan.tierNumber >= 5"
          v-bind="plan"
          @planChange="selectPlan"
        />
      </Carousel>
    </div>
  </div>
</template>

<script>
import { RadioToggle, TypeBody, TypeDisplay, BaseIcon } from '@bambeehr/pollen';
import { computed, defineComponent, watch, ref } from '@nuxtjs/composition-api';
import PlanBlock from '@/modules/InformationPages/Pricing/components/PlanBlock';
import usePricePlans from '@/hooks/usePricePlans';
import Pills from '@/components/Pills.vue';
import EmployeeSelect from '@/components/EmployeeSelect.vue';
import Carousel from '@/components/Carousel.vue';

export default defineComponent({
  name: 'PricingPlans',
  components: {
    PlanBlock,
    RadioToggle,
    TypeBody,
    TypeDisplay,
    Pills,
    BaseIcon,
    EmployeeSelect,
    Carousel,
  },
  setup() {
    const currentPlan = ref();
    const scrollTo = ref();
    const { monthlyPlans, dedicatedPlans } = usePricePlans();

    const unwatch = watch(
      monthlyPlans,
      (n, o) => {
        if (n) {
          currentPlan.value = n[0]?.value;
          unwatch && unwatch();
        }
      },
      { immediate: true }
    );

    const isReady = computed(() => !!currentPlan.value);

    const getPlanByTier = (tierNumber = 0) => {
      return monthlyPlans.value.find((plan) => plan.tierNumber === tierNumber);
    };
    const pricingPlanBocks = computed(() => {
      const blocks = [
        {
          ...getPlanByTier(0),
          ...{
            title: `No Employees Yet`,
            pillLabel: `${getPlanByTier(0)?.maxSize}`,
          },
          subtitle:
            'A plan built to help you hire your first employee compliantly',
          features: [
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: 'Access to Bambee HR',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '4 Core HR Policies',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '2 Custom HR Policy',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '2 Job Descriptions',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: 'Chat and email support',
            },
          ],
        },
        {
          ...getPlanByTier(1),
          ...{
            title: `${getPlanByTier(1)?.employeeSizeMapping} Employees`,
            pillLabel: getPlanByTier(1)?.employeeSizeMapping,
          },
          subtitle:
            'Build confidence in the choices you make related to HR & People',
          features: [
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: 'Dedicated HR Manager',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: `HR support for up to ${
                getPlanByTier(1)?.maxSize
              } employees`,
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '4 Core HR Policies',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '2 Custom HR Policies',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '3 Job Descriptions',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: 'Chat, email, and phone support',
            },
          ],
        },
        {
          ...getPlanByTier(2),
          ...{
            title: `${getPlanByTier(2)?.employeeSizeMapping} Employees`,
            pillLabel: getPlanByTier(2)?.employeeSizeMapping,
          },
          subtitle:
            'Build confidence in the choices you make related to HR & People',
          features: [
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: 'Dedicated HR Manager',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: `HR support for up to ${
                getPlanByTier(2)?.maxSize
              } employees`,
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '4 Core HR Policies',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '4 Custom HR Policies',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '4 Job Descriptions',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: 'Chat, email, and phone support',
            },
          ],
        },
        {
          ...getPlanByTier(3),
          ...{
            title: `${getPlanByTier(3)?.employeeSizeMapping} Employees`,
            pillLabel: getPlanByTier(3)?.employeeSizeMapping,
          },
          subtitle:
            'Grow teams that operate better with HR at their side inside their businesses',
          features: [
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: 'Dedicated HR Manager',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: `HR support for up to ${
                getPlanByTier(3)?.maxSize
              } employees`,
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '4 Core HR Policies',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '8 Custom HR Policies',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '5 Job Descriptions',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: 'Chat, email, and phone support',
            },
          ],
        },
        {
          ...getPlanByTier(4),
          ...{
            title: `${getPlanByTier(4)?.employeeSizeMapping} Employees`,
            pillLabel: getPlanByTier(4)?.employeeSizeMapping,
          },
          subtitle:
            'Grow teams that operate better with HR at their side inside their businesses',
          features: [
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: 'Dedicated HR Manager',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: `HR support for up to ${
                getPlanByTier(4)?.maxSize
              } employees`,
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '4 Core HR Policies',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '12 Custom HR Policies',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '5 Job Descriptions',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: 'Chat, email, and phone support',
            },
          ],
        },
        {
          ...getPlanByTier(5),
          ...{
            title: `${getPlanByTier(5)?.minSize}+ Employees`,
            pillLabel: `${getPlanByTier(5)?.minSize}+`,
          },
          subtitle: 'Custom pricing to meet the needs of your growing business',
          features: [
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: 'Dedicated HR Manager',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: `HR support for up to ${
                getPlanByTier(5)?.maxSize
              } employees`,
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '4 Core HR Policies',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '12 Custom HR Policies',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: '5 Job Descriptions',
            },
            {
              component: BaseIcon,
              icon: 'checkCircleSolid',
              text: 'Chat, email, and phone support',
            },
          ],
        },
      ];

      return blocks.filter((b) => !!b.value);
    });

    const planTabs = computed(() => {
      return pricingPlanBocks.value.map(({ pillLabel, value }) => {
        return {
          label: pillLabel,
          value,
        };
      });
    });

    const selectPlan = (planValue) => {
      currentPlan.value = planValue;
      const el = document.querySelector(`#plan-${planValue}`);
      scrollTo.value = el.offsetLeft;
    };

    const handleTabChange = (planValue) => {
      selectPlan(planValue);
    };

    const planOptionsDropdown = computed(() => {
      return planTabs.value.map((pt) => ({
        ...pt,
        label: `${pt.label} Employees`,
      }));
    });

    return {
      pricingPlans: pricingPlanBocks,
      planTabs,
      planOptionsDropdown,
      currentPlan,
      handleTabChange,
      scrollTo,
      isReady,
      selectPlan,
    };
  },
});
</script>

<style scoped>
.plan-block {
  scroll-margin-top: 50px;
  @apply sm:w-1/2 lg:w-auto lg:mx-2;
}
</style>
